import { useState, type FormEvent } from 'react';
import { ClipboardList, GraduationCap, AlertCircle } from 'lucide-react';
import { useAuthMethods, useDevLogin } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { OtpPanel } from './OtpPanel';
import { MicrosoftLogo } from './MicrosoftLogo';
import { loginCopy } from './loginCopy';

/**
 * Login page. Renders the Microsoft SSO button when OIDC is
 * configured, and the dev username form only when the API reports
 * dev login is active. /api/auth/methods is the source of truth so
 * production builds don't leak a non-functional dev form.
 *
 * Shared by both portals. The wording, brand mark and whether the
 * one-time-code panel appears come from loginCopy(portal): the training
 * host is SSO-only for existing staff, the main host keeps the new-starter
 * paths. The portal is read from the same /methods probe so nothing is
 * decided client-side from the URL.
 *
 * Visual brief: this is the very first impression for any new
 * user. Centred card on a soft gradient, brand mark, generous
 * spacing, and a clear primary CTA.
 */
export function Login() {
  const methods = useAuthMethods();
  const devLogin = useDevLogin();
  const [email, setEmail] = useState('dev@local.test');
  const [password, setPassword] = useState('dev');
  const [error, setError] = useState<string | null>(null);

  const portal = methods.data?.portal ?? 'main';
  const copy = loginCopy(portal);

  const showDev = methods.data?.dev_login_active === true;
  const showOidc = methods.data?.oidc_configured === true;
  const showOtp = methods.data?.otp_login_enabled === true && copy.offerOtp;

  // The OIDC callback bounces a blocked/archived account back here with a
  // flag rather than showing a raw 403, so surface a clear reason.
  const blockedNotice = (() => {
    const v = new URLSearchParams(window.location.search).get('blocked');
    if (v === 'disabled') {
      return 'This account has been disabled. Please contact your administrator.';
    }
    if (v === 'inactive') {
      return 'This account is no longer active. Please contact HR.';
    }
    if (v === 'unknown') {
      return 'No account has been set up for this email. Please contact your administrator.';
    }
    return null;
  })();

  async function onDevSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await devLogin.mutateAsync({ email, password });
      window.location.href = '/';
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('login failed');
      }
    }
  }

  return (
    <main className="min-h-screen p-6 text-fg" style={{ background: 'var(--hero-gradient), rgb(var(--bg))' }}>
      <div className="mx-auto mt-12 max-w-md sm:mt-20">
        <div className="mb-6 flex flex-col items-center text-center">
          <div
            className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-fg-on-accent shadow-token-accent"
            style={{ backgroundImage: 'linear-gradient(135deg, rgb(var(--accent)) 0%, #ec4899 100%)' }}
          >
            {portal === 'training' ? (
              <GraduationCap className="h-7 w-7" strokeWidth={2} />
            ) : (
              <ClipboardList className="h-7 w-7" strokeWidth={2} />
            )}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
          <p className="mt-1 text-sm text-fg-muted">{copy.subtitle}</p>
        </div>

        <div className="rounded-2xl border border-line/10 bg-surface p-6 shadow-lg sm:p-8">
          {blockedNotice ? (
            <div className="mb-5 flex items-start gap-2 rounded-md bg-warning-soft/15 px-3 py-2 text-sm text-warning-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{blockedNotice}</span>
            </div>
          ) : null}
          {showOidc ? (
            <a
              href="/api/auth/oidc/login"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#2F2F2F] px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-black"
            >
              <MicrosoftLogo className="h-4 w-4" />
              <span>Sign in with Microsoft</span>
            </a>
          ) : null}

          {showOidc && showDev ? (
            <div className="my-5 flex items-center gap-3 text-[11px] font-medium uppercase tracking-wider text-fg-dim">
              <hr className="flex-1 border-line/10" />
              <span>or</span>
              <hr className="flex-1 border-line/10" />
            </div>
          ) : null}

          {showDev ? (
            <form onSubmit={onDevSubmit} className="space-y-4">
              <label className="block text-sm">
                <span className="font-medium text-fg-muted">Email</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-fg-muted">Password</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 block h-11 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm transition-colors focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
                />
              </label>

              {error ? (
                <div className="flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                fullWidth
                loading={devLogin.isPending}
              >
                {devLogin.isPending ? 'Signing in...' : 'Sign in (dev only)'}
              </Button>
              <p className="text-center text-xs text-fg-muted">
                Username login is enabled only in development mode.
              </p>
            </form>
          ) : null}

          {showOtp && (showOidc || showDev) ? (
            <div className="my-5 flex items-center gap-3 text-[11px] font-medium uppercase tracking-wider text-fg-dim">
              <hr className="flex-1 border-line/10" />
              <span>or</span>
              <hr className="flex-1 border-line/10" />
            </div>
          ) : null}

          {showOtp ? <OtpPanel /> : null}

          {methods.isPending ? (
            <div className="h-11 w-full animate-pulse rounded-md bg-surface-2" />
          ) : null}

          {methods.isSuccess && !showOidc && !showDev && !showOtp ? (
            <div className="flex items-start gap-2 rounded-md bg-warning-soft/15 px-3 py-2 text-sm text-warning-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>No sign-in method is configured. Contact {copy.helpContact}.</span>
            </div>
          ) : null}
        </div>

        <p className="mt-6 text-center text-xs text-fg-muted">
          Need help? Contact {copy.helpContact}.
        </p>
      </div>
    </main>
  );
}
